import { useState } from 'react';
import {
  Shield,
  Lock,
  MessageSquare,
  Mic,
  Tag,
  Check,
  ArrowRight,
  Menu,
  XCircle,
  BookOpen,
  Camera,
  Paperclip,
  QrCode,
  Bell,
  MapPin,
  Zap,
  FileText,
  Skull,
  Database,
  CloudIcon
} from 'lucide-react';
import { FeatureFlow } from './FeatureFlow';
import { SEOHead } from './SEOHead';

const SupabaseIcon = ({ className }: { className?: string }) => (
  <img src="https://assets.shellx.app/supabase.svg" alt="Supabase" width="24" height="24" loading="lazy" className={className} />
);

const CloudflareIcon = ({ className }: { className?: string }) => (
  <img src="https://assets.shellx.app/cloudflare.svg" alt="Cloudflare" width="24" height="24" loading="lazy" className={className} />
);

const NetlifyIcon = ({ className }: { className?: string }) => (
  <img src="https://assets.shellx.app/netlify.svg" alt="Netlify" width="24" height="24" loading="lazy" className={className} />
);

export function LandingPage({ onGetStarted, onLogin }: { onGetStarted: () => void; onLogin: () => void }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A] transition-colors">
      <SEOHead
        title="ShellX - Mobile Interface for Your n8n Workflows"
        description="Connect your n8n, Make, or Zapier workflows to a professional mobile app. Voice commands, QR scanning, push notifications. Zero data stored. Free forever."
        path="/"
      />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-slate-800 transform translate-z-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://assets.shellx.app/logo.png" alt="ShellX" className="w-10 h-10 rounded-xl border border-slate-700" />
              <span className="text-2xl font-bold text-white">ShellX</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#your-webhook" className="text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Your webhook
              </a>
              <a href="#features" className="text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Features
              </a>
              <a href="#company-mode" className="text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Teams
              </a>
              <a href="#pricing" className="text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Pricing
              </a>
              <a
                href="/help"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-300 hover:text-[#8B5CF6] transition font-medium"
              >
                <BookOpen className="w-4 h-4" />
                Docs
              </a>
              <button
                onClick={onLogin}
                className="px-5 py-2 text-slate-300 hover:text-white rounded-lg font-semibold transition"
              >
                Login
              </button>
              <button
                onClick={onGetStarted}
                className="px-5 py-2 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-lg font-semibold hover:from-[#7C3AED] hover:to-[#2563EB] transition shadow-lg shadow-purple-500/30"
              >
                Get Started
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-800"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <XCircle className="w-6 h-6 text-slate-300" /> : <Menu className="w-6 h-6 text-slate-300" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-6 space-y-4">
              <a href="#your-webhook" className="block text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Your webhook
              </a>
              <a href="#features" className="block text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Features
              </a>
              <a href="#company-mode" className="block text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Teams
              </a>
              <a href="#pricing" className="block text-slate-300 hover:text-[#8B5CF6] transition font-medium">
                Pricing
              </a>
              <a
                href="/help"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-300 hover:text-[#8B5CF6] transition font-medium"
              >
                <BookOpen className="w-4 h-4" />
                Documentation
              </a>
              <button
                onClick={onLogin}
                className="w-full px-6 py-2.5 border border-slate-700 text-slate-300 rounded-lg font-semibold hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition"
              >
                Login
              </button>
              <button
                onClick={onGetStarted}
                className="w-full px-6 py-2.5 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-lg font-semibold"
              >
                Get Started
              </button>
            </div>
          )}
        </div>
      </nav>

      <main>
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-1 lg:order-1">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 md:mb-8 leading-[1.1] tracking-tight">
                <span className="text-white text-5xl md:text-7xl lg:text-8xl">
                  Finally.
                </span>
                <br />
                <span className="text-white">A Professional Interface</span><br />
                <span className="text-white">For </span>
                <span className="text-red-400">Your n8n</span>
                <span className="text-white"> Workflows.</span>
              </h1>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
                <a
                  href="https://apps.apple.com/nl/app/shellx/id6758803722"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:scale-105 transition-transform relative group"
                >
                  <div className="absolute -inset-1 bg-white/10 rounded-xl blur-lg group-hover:bg-white/20 transition"></div>
                  <div className="relative bg-white border border-slate-600 rounded-lg overflow-hidden w-fit">
                    <img
                      src="https://assets.shellx.app/app-store-dark_1737618798.png"
                      alt="Download on the App Store"
                      className="h-[40px] md:h-[48px] block"
                    />
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=app.shellx.android"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:scale-105 transition-transform relative group"
                >
                  <div className="absolute -inset-1 bg-white/10 rounded-xl blur-lg group-hover:bg-white/20 transition"></div>
                  <div className="relative bg-white border border-slate-600 rounded-lg overflow-hidden w-fit">
                    <img
                      src="https://assets.shellx.app/play-store-dark_1737618858.png"
                      alt="Get it on Google Play"
                      className="h-[40px] md:h-[48px] block"
                    />
                  </div>
                </a>
              </div>
              <p className="text-xs md:text-sm text-slate-300">
                100% free. All features included.
              </p>
            </div>

            <div className="order-2 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-[280px] sm:max-w-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] rounded-3xl blur-3xl opacity-20 will-change-transform"></div>
                <img
                  src="https://assets.shellx.app/hero.png"
                  alt="ShellX App Interface"
                  width="1170"
                  height="2532"
                  fetchPriority="high"
                  className="relative rounded-3xl shadow-2xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="your-webhook" className="py-12 md:py-20 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-12 lg:mb-16 items-center">
            <div className="order-1 lg:order-2 lg:col-span-2">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
                Zero Trust: Direct connection.
              </h2>
              <p className="text-xl md:text-2xl font-semibold text-slate-300 mb-6 md:mb-8">
                Your webhook, your rules.
              </p>

              <p className="text-base md:text-xl text-slate-300 mb-6">
                We never see your data. Not even for a second. We're just the interface between your device and your infrastructure.
              </p>

              <div className="flex flex-wrap gap-x-6 md:gap-x-8 gap-y-3 md:gap-y-4 text-sm md:text-base text-slate-300">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />
                  <span>No selling</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />
                  <span>No mining</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />
                  <span>No training</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />
                  <span>No advertisement</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-400 flex-shrink-0" />
                  <span className="font-semibold">Store conversation history locally</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-400 flex-shrink-0" />
                  <span className="font-semibold">Your data never touches our infrastructure.</span>
                </div>
              </div>
            </div>

            <div className="order-2 lg:order-1 lg:col-span-1 flex justify-center">
              <div className="relative w-full max-w-[280px] sm:max-w-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl blur-2xl opacity-20 will-change-transform"></div>
                <img
                  src="https://assets.shellx.app/webhook.png"
                  alt="Direct Webhook Connection"
                  width="1170"
                  height="2532"
                  loading="lazy"
                  className="relative w-full rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 px-6 bg-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Your Phone's Superpowers.<br />
              Connected to Your Workflows.
            </h2>
            <p className="text-xl text-slate-300">
              Everything your device can capture → your automation can process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-violet-500/50 transition-all hover:shadow-lg hover:shadow-violet-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Voice Commands</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Say:</span> "Add this to my todo list"
                </p>
                <p className="text-emerald-300 text-sm">
                  <span className="font-semibold">Result:</span> Task created in Notion/Asana/etc
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Hands-free automation at your command.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Tag className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Multi-Workflow Labels</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">One tap:</span> [Work] → [Home] → [Client A]
                </p>
                <p className="text-emerald-300 text-sm">
                  Different context, different agent.
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Switch workflows like you switch hats.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-emerald-500/50 transition-all hover:shadow-lg hover:shadow-emerald-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Chat Mode</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Ask:</span> "What were our Q4 sales numbers?"
                </p>
                <p className="text-emerald-300 text-sm">
                  <span className="font-semibold">Get:</span> Real data from your systems.
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Conversational access to your company data.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-orange-500/50 transition-all hover:shadow-lg hover:shadow-orange-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <QrCode className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">QR & Barcode Scanner</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Scan:</span> Product barcode
                </p>
                <p className="text-emerald-300 text-sm">
                  <span className="font-semibold">Done:</span> Inventory updated instantly.
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Visual input, automatic logging.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-pink-500/50 transition-all hover:shadow-lg hover:shadow-pink-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Photo Uploads</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Snap:</span> Job site photo
                </p>
                <p className="text-emerald-300 text-sm">
                  <span className="font-semibold">Say:</span> "Make a report including this"
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Photos become documentation automatically.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Document Uploads</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Upload:</span> Product spec PDF
                </p>
                <p className="text-emerald-300 text-sm">
                  <span className="font-semibold">Ask:</span> "Create marketing copy for this"
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Documents become actionable input.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">GPS Location</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Command:</span> "Add this stop to my route"
                </p>
                <p className="text-emerald-300 text-sm">
                  <span className="font-semibold">Result:</span> Location saved, route optimized.
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Location-aware automation on the go.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-red-500/50 transition-all hover:shadow-lg hover:shadow-red-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Push Notifications</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Workflow runs →</span> You get alerted
                </p>
                <p className="text-emerald-300 text-sm">
                  "Report ready ✓" "Backup done ✓"
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Stay informed without checking constantly.
              </p>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 border border-slate-700 hover:border-sky-500/50 transition-all hover:shadow-lg hover:shadow-sky-500/20 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Quick Mode</h3>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-slate-300 text-sm">
                  <span className="font-semibold">Command:</span> "Turn on the lights"
                </p>
                <p className="text-emerald-300 text-sm">
                  <span className="font-semibold">Gone:</span> No chat history clutter.
                </p>
              </div>
              <p className="text-slate-300 text-sm">
                Fast commands without conversation mess.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-slate-900 overflow-hidden">
        <div className="max-w-7xl mx-auto mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
            Works With Your Stack
          </h2>
          <p className="text-lg text-slate-300 text-center">
            Integrate seamlessly with your favorite automation platforms and tools
          </p>
        </div>

        <div className="relative">
          <div className="flex gap-12 animate-scroll">
            <div className="flex gap-12 min-w-max">
              <img src="https://cdn.simpleicons.org/n8n/white" alt="n8n" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/make/6D4AFF" alt="Make" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/zapier/FF4A00" alt="Zapier" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/cloudflare/F38020" alt="Cloudflare" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/ifttt/000000" alt="IFTTT" className="h-12 w-12 opacity-60 hover:opacity-100 transition bg-white rounded-sm" />
              <img src="https://cdn.simpleicons.org/retool/3D3D3D" alt="Retool" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/appsmith/white" alt="Appsmith" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/budibase/white" alt="Budibase" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/homeassistant/18BCF2" alt="Home Assistant" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/nodered/8F0000" alt="Node-RED" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/raspberrypi/A22846" alt="Raspberry Pi" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/espressif/E7352C" alt="ESP32" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/supabase/3FCF8E" alt="Supabase" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/cloudflareworkers/F38020" alt="Cloudflare Workers" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
            </div>
            <div className="flex gap-12 min-w-max" aria-hidden="true">
              <img src="https://cdn.simpleicons.org/n8n/white" alt="n8n" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/make/6D4AFF" alt="Make" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/zapier/FF4A00" alt="Zapier" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/cloudflare/F38020" alt="Cloudflare" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/ifttt/000000" alt="IFTTT" className="h-12 w-12 opacity-60 hover:opacity-100 transition bg-white rounded-sm" />
              <img src="https://cdn.simpleicons.org/retool/3D3D3D" alt="Retool" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/appsmith/white" alt="Appsmith" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/budibase/white" alt="Budibase" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/homeassistant/18BCF2" alt="Home Assistant" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/nodered/8F0000" alt="Node-RED" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/raspberrypi/A22846" alt="Raspberry Pi" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/espressif/E7352C" alt="ESP32" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/supabase/3FCF8E" alt="Supabase" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
              <img src="https://cdn.simpleicons.org/cloudflareworkers/F38020" alt="Cloudflare Workers" className="h-12 w-12 opacity-60 hover:opacity-100 transition" />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-8">
          <p className="text-lg text-slate-300 text-center">
            Or any webhook you want
          </p>
        </div>
      </section>

      <section id="company-mode" className="py-12 md:py-20 px-6 bg-white transform translate-z-0">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 md:mb-6">
                Teams:<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
                  IT-Controlled Deployment
                </span>
              </h2>

              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <p className="text-lg md:text-xl text-slate-600">No manual configuration.</p>
                <p className="text-lg md:text-xl text-slate-600">No shared credentials.</p>
                <p className="text-lg md:text-xl text-slate-600">No security nightmares.</p>
              </div>

              <div className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                <div className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-slate-700">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-600 flex-shrink-0" />
                  <span>QR enrollment (no manual setup)</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-slate-700">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-600 flex-shrink-0" />
                  <span>Central access control</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-slate-700">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-600 flex-shrink-0" />
                  <span>Remote revocation & data wipe</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-slate-700">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-600 flex-shrink-0" />
                  <span>Encrypted webhooks</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-slate-700">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-emerald-600 flex-shrink-0" />
                  <span>Custom branding</span>
                </div>
              </div>

              <div className="inline-block">
                <p className="text-xl md:text-2xl font-bold text-slate-900 mb-2">Scan QR. Instant Setup.</p>
                <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full will-change-transform"></div>
              </div>
            </div>

            <div className="order-2 lg:order-1 flex justify-center">
              <div className="w-full max-w-[200px] sm:max-w-[250px] lg:max-w-sm">
                <img
                  src="https://assets.shellx.app/teams.png"
                  alt="Company Mode QR Enrollment"
                  width="1170"
                  height="2532"
                  loading="lazy"
                  className="rounded-2xl shadow-2xl border border-slate-200 w-full transform translate-z-0"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white transform translate-z-0">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why Companies Choose Teams
            </h2>
            <p className="text-2xl text-slate-600">
              What makes Teams different
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Skull className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">True Remote Wipe</h3>
                  <p className="text-slate-700 mb-1">
                    Revoke access → configuration wiped within 15 minutes. App resets to welcome screen.
                  </p>
                  <p className="text-slate-600 text-sm">
                    Employee can't reconnect. Guaranteed.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Encrypted by Design</h3>
                  <p className="text-slate-700 mb-1">
                    Your configuration encrypted with your key. We store blobs. You hold the only key.
                  </p>
                  <p className="text-slate-600 text-sm">
                    We can't read it. Even if we wanted to.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Zero Manual Updates</h3>
                  <p className="text-slate-700 mb-1">
                    Change webhooks/setup/branding centrally. All devices update automatically.
                  </p>
                  <p className="text-slate-600 text-sm">
                    Update 500 phones without touching one.
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <p className="text-2xl font-bold text-slate-900">
                  This is why IT is happy with ShellX
                </p>
              </div>
            </div>

            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-full max-w-[280px] sm:max-w-sm lg:max-w-md">
                <img
                  src="https://assets.shellx.app/config.png"
                  alt="Configuration Dashboard"
                  width="1170"
                  height="2532"
                  loading="lazy"
                  className="rounded-2xl shadow-2xl border-2 border-slate-200 w-full transform translate-z-0"
                />
                <img
                  src="https://assets.shellx.app/QR.png"
                  alt="QR Code Enrollment"
                  width="1170"
                  height="2532"
                  loading="lazy"
                  className="absolute -right-4 sm:-right-8 top-1/2 -translate-y-1/2 rounded-xl shadow-2xl border-2 border-slate-200 w-32 sm:w-40 lg:w-48 will-change-transform"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 px-6 bg-slate-50 transform translate-z-0">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Simple Pricing
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 transform translate-z-0 will-change-transform">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">FREE</h3>
                <p className="text-slate-600 mb-4">Personal</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-slate-900">€0</span>
                  <span className="text-slate-600">forever</span>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Unlimited messages</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Voice & text input</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">All input features</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Local storage</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Push notifications</span>
                </div>
              </div>



              <div className="flex flex-row gap-3 items-center justify-center">
                <a
                  href="https://apps.apple.com/nl/app/shellx/id6758803722"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:scale-105 transition-transform relative group"
                >
                  <div className="absolute -inset-1 bg-slate-300/30 rounded-xl blur-lg group-hover:bg-slate-300/50 transition"></div>
                  <div className="relative border border-slate-300 rounded-lg overflow-hidden">
                    <img
                      src="https://assets.shellx.app/app-store-dark_1737618798.png"
                      alt="Download on the App Store"
                      className="h-[40px] block"
                    />
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=app.shellx.android"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:scale-105 transition-transform relative group"
                >
                  <div className="absolute -inset-1 bg-slate-300/30 rounded-xl blur-lg group-hover:bg-slate-300/50 transition"></div>
                  <div className="relative border border-slate-300 rounded-lg overflow-hidden">
                    <img
                      src="https://assets.shellx.app/play-store-dark_1737618858.png"
                      alt="Get it on Google Play"
                      className="h-[40px] block"
                    />
                  </div>
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-8 relative overflow-hidden transform translate-z-0 will-change-transform">
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                <span className="text-xs font-bold text-white">POPULAR</span>
              </div>

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">TEAMS</h3>
                <p className="text-blue-100 mb-4">Business</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-white">€5</span>
                  <span className="text-blue-100">/seat/month</span>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-white font-semibold mb-3">Everything in Free, plus:</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-blue-50">QR enrollment</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-blue-50">Central management</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-blue-50">Remote wipe</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-blue-50">Encrypted config</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-blue-50">Custom branding</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-blue-100">Minimum 5 seats</p>
              </div>

              <button
                onClick={onGetStarted}
                className="w-full px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition transform translate-z-0"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white transform translate-z-0">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Who Needs Teams?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 transform translate-z-0 will-change-transform">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Automation Agencies
              </h3>

              <p className="text-slate-700 mb-4">
                You build workflows, clients see... a webhook URL. Not exactly impressive.
              </p>

              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">With client branding</span>
                </div>
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Deliver a professional mobile interface</span>
                </div>
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Turn projects into recurring revenue</span>
                </div>
              </div>
           <div className="mb-6">
                <p className="text-lg font-semibold text-slate-900">Starting at €100/month (20 seats)</p>
              </div>

              
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 transform translate-z-0 will-change-transform">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                The Power of Safe AI
              </h3>

              <p className="text-slate-700 mb-4">
                Keys to your kingdom stay home.
              </p>

              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Chat with your data like it's ChatGPT</span>
                </div>
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Host your own infrastructure<br /><span className="text-slate-600">(cloud or on-premise)</span></span>
                </div>
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Zero vendor lock-in. Switch anytime.</span>
                </div>
              </div>

   

              <div className="pt-4 border-t border-slate-300 will-change-transform">
                <p className="text-lg font-semibold text-slate-900">Make your automation sexy</p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 transform translate-z-0 will-change-transform">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Field Service Companies
              </h3>

              <p className="text-slate-700 mb-4">
                50+ technicians need mobile work orders. IT can't share webhook URLs (security).
              </p>

              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">QR enrollment per technician</span>
                </div>
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Revoke when they leave</span>
                </div>
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Change webhooks without their involvement</span>
                </div>
              </div>

           <div className="mb-6">
                <p className="text-lg font-semibold text-slate-900">AI without compromise.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

<section className="py-20 px-6 bg-white overflow-hidden transform translate-z-0">
  <div className="max-w-6xl mx-auto">
    <div className="text-center mb-16">
      <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
        Built on Trusted Infrastructure
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
      {/* Supabase Card */}
      <div className="group text-center p-8 rounded-3xl transition-all hover:bg-slate-50">
        <div className="mb-6 relative">
          <div className="absolute inset-0 bg-[#3ECF8E]/10 blur-3xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 will-change-transform"></div>
          <SupabaseIcon className="w-20 h-20 mx-auto relative transition-transform duration-300 group-hover:scale-110" />
        </div>
        <h3 className="text-2xl font-bold mb-3 text-[#3ECF8E]">Supabase</h3>
        <div className="space-y-2 text-slate-600">
          <p className="font-medium">Database & Auth</p>
          <p className="font-medium text-sm opacity-80">SOC2 Type II • EU Residency</p>
        </div>
      </div>

      {/* Cloudflare Card */}
      <div className="group text-center p-8 rounded-3xl transition-all hover:bg-slate-50">
        <div className="mb-6 relative">
          <div className="absolute inset-0 bg-[#F6821F]/10 blur-3xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 will-change-transform"></div>
          <CloudflareIcon className="w-20 h-20 mx-auto relative transition-transform duration-300 group-hover:scale-110" />
        </div>
        <h3 className="text-2xl font-bold mb-3 text-[#F6821F]">Cloudflare</h3>
        <div className="space-y-2 text-slate-600">
          <p className="font-medium">Security & CDN</p>
          <p className="font-medium text-sm opacity-80">DDoS Protection • Enterprise</p>
        </div>
      </div>

      {/* Netlify Card */}
      <div className="group text-center p-8 rounded-3xl transition-all hover:bg-slate-50">
        <div className="mb-6 relative">
          <div className="absolute inset-0 bg-[#00C7B7]/10 blur-3xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 will-change-transform"></div>
          <NetlifyIcon className="w-20 h-20 mx-auto relative transition-transform duration-300 group-hover:scale-110" />
        </div>
        <h3 className="text-2xl font-bold mb-3 text-[#00C7B7]">Netlify</h3>
        <div className="space-y-2 text-slate-600">
          <p className="font-medium">Global Hosting</p>
          <p className="font-medium text-sm opacity-80">Edge Network • 99.99% Uptime</p>
        </div>
      </div>
    </div>

    <div className="text-center mt-12">
      <p className="text-xl text-slate-300 italic font-light">
        "We stand on the shoulders of giants."
      </p>
    </div>
  </div>
</section>

      <section className="py-20 px-6 bg-gradient-to-br from-[#0A0A0A] to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <BookOpen className="w-16 h-16 mx-auto text-[#8B5CF6] mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Explore our comprehensive documentation to learn everything about ShellX features, setup guides, and best practices.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="/help"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-xl font-semibold hover:from-[#7C3AED] hover:to-[#2563EB] transition shadow-lg shadow-purple-500/30 flex items-center gap-2"
            >
              <BookOpen className="w-5 h-5" />
              View Documentation
            </a>
            <button
              onClick={onGetStarted}
              className="px-8 py-4 border-2 border-[#8B5CF6] text-white rounded-xl font-semibold hover:bg-[#8B5CF6]/10 transition"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>
      </main>
    </div>
  );
}
